import React, { useEffect, useState } from "react";
import { observe } from "selector-observer";
import { render } from "react-dom";
import axios from 'axios';

// Helper function to format the epicDataLastUpdatedAt
const formatEpicDataLastUpdatedAt = (dateTimeStr: string) => {
    if (!dateTimeStr) return null;
    const dateTime = new Date(dateTimeStr);
    return dateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
}

// Utility function to calculate WaitTimeCategory
const calculateWaitTimeCategory = (waitTimeInMinutes: number | null) => {
    if (waitTimeInMinutes === null) {
        return "Unknown wait time";
    }
    if (waitTimeInMinutes < 15) {
        return "Approximate Wait";
    } else if (waitTimeInMinutes >= 15 && waitTimeInMinutes <= 45) {
        return "Approximate Wait";
    } else if (waitTimeInMinutes > 45) {
        return "Approximate Wait";
    }
    return "Unknown wait time";
};

// Utility function to get the color based on WaitTimeCategory
const getColorForWaitTimeCategory = (waitTimeInMinutes: number | null) => {
    if (waitTimeInMinutes === null) {
        return "#BCBBBA"; // Default to Cool Gray 4 for unknown wait times
    }
    if (waitTimeInMinutes < 15) {
        return "#008E4A"; // Brand Primary Green
    } else if (waitTimeInMinutes >= 15 && waitTimeInMinutes <= 45) {
        return "#FFCB00"; // Brand Primary Yellow
    } else if (waitTimeInMinutes > 45) {
        return "#FF7F00"; // Brand Orange
    }
    return "#BCBBBA"; // Default to Cool Gray 4 for unknown wait times
};

// Helper function to format the wait time
const formatWaitTime = (waitTimeInMinutes: number | null) => {
    if (waitTimeInMinutes === null) {
        return "Unknown wait time";
    }
    if (waitTimeInMinutes < 15) {
        return "Less than 15 minutes";
    } else if (waitTimeInMinutes >= 15 && waitTimeInMinutes <= 45) {
        return "15 to 45 minutes";
    } else if (waitTimeInMinutes > 45) {
        return "More than 45 minutes";
    }
    return "Unknown wait time";
}


declare global {
    interface Window {
        localization: {
            EDWaitTimeLabel: string;
            EDWaitTimeLabelNoData: string;
           
        };
    }
}
// LocationCard Component
export const LocationCard = (props: any) => {
    const {
        title,
        waitTime,
        formattedWaitTime,
        waitTimeCategory,
        formattedEpicDataLastUpdatedAt,
        body,
        waitTimeCategoryColor,
        edLocation
    } = props;

    console.log(`WAIT TIME LOCATION: ${title} + ${waitTime} +  CATEGORY: ${waitTimeCategory} + COLOR: ${waitTimeCategoryColor} + FWT: ${formattedWaitTime}`);

    // Type-safe localization with fallback
    const localization = window.localization || {
        EDWaitTimeLabel: 'Approximate Time',
        EDWaitTimeLabelNoData: 'Wait time data currently unavailable.',
        ViewMoreLinkLabel: 'View More'
    };

    return (
        <div className="ed-wait-time">
            <header>
                {title && <h3>{title}</h3>}
            </header>
            <div className="ed-wait-time-content">
                {/* Conditionally render wait time or fallback message */}
                {waitTime ? (
                    <div className="wait-time-col">
                        <div className="wait-time-content">
                            <i className="fas fa-clock wait-time-clock" style={{ color: waitTimeCategoryColor ?? '' }}></i>
                            <div className="wait-time-text">
                                <h4>{waitTimeCategory ?? localization.EDWaitTimeLabel}</h4>
                                <p className="wait-time-data-text">{formattedWaitTime ?? ''}</p>
                            </div>
                        </div>
                        <div style={{ marginLeft: '3.6rem', display: 'flex', alignItems: 'center' }}>
                            <p style={{ fontStyle: 'italic', fontSize: '1rem', margin: '0.2rem -0.5rem' }}>
                                Data last updated {formattedEpicDataLastUpdatedAt ?? ''}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="no-wait-time-available">
                        <i className="fas fa-info-circle wait-time-icon" style={{ color: '#BCBBBA' }}></i>
                            <p>Wait time data currently unavailable</p>
                    </div>
                )}
                {/* Always render the body content for location details */}
                {body && <div dangerouslySetInnerHTML={{ __html: body }} />}
            </div>
        </div>
    );
};

// LocationCards Component
export const LocationCards = (props: any) => {
    const { edLocations } = props;
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showSpinner, setShowSpinner] = useState(true);
    let loadCount = 0; // Track the load count

    const fetchData = async (location: any) => {
        try {
            const response = await axios.get(`/api/EdWaitTimes/edlocation/${location.edLocation}?t=${new Date().getTime()}`);
            console.log("API response:", response.data);
            return response.data;
        } catch (error) {
            if (error.response && error.response.status === 404) {
                console.warn(`404 Error for ${location.edLocation}, using fallback.`);
                // Handle 404 by returning a fallback
                return {
                    edLocation: location.edLocation,
                    title: location.edLocation,
                    waitTimeInMinutes: null,
                    body: location.body // Use the body passed in from the location object
                };
            }
            console.error(`Error fetching data for ${location.edLocation}:`, error);
            return null;
        }
    };

    const loadAllLocations = async () => {
        setLoading(true);
        setShowSpinner(true); // Show spinner when starting data load

        const locationsData = await Promise.all(
            edLocations.map(async (location: any) => {
                const data = await fetchData(location); // Pass 'location' to 'fetchData'

                if (data && data.waitTimeInMinutes !== undefined) {
                    const waitTimeCategory = calculateWaitTimeCategory(data.waitTimeInMinutes);
                    const waitTimeCategoryColor = getColorForWaitTimeCategory(data.waitTimeInMinutes);
                    const formattedWaitTime = formatWaitTime(data.waitTimeInMinutes);
                    const formattedEpicDataLastUpdatedAt = formatEpicDataLastUpdatedAt(data.epicDataLastUpdatedAt);

                    console.log(`CHANGED: WAIT TIME LOCATION: ${location.edLocation} + ${data.waitTimeInMinutes} +  CATEGORY: ${waitTimeCategory} + COLOR: ${waitTimeCategoryColor} + FORMATTEDWAITTIME: ${formattedWaitTime}`);

                    return {
                        ...location,
                        ...data,
                        waitTimeCategory,
                        waitTimeCategoryColor,
                        formattedWaitTime,
                        formattedEpicDataLastUpdatedAt
                    };
                }

                return {
                    edLocation: location.edLocation,
                    title: location.edLocation,
                    waitTimeInMinutes: null,
                    body: location.body // Keep original body content
                };
            })
        );

        // Filter out null locations
        setCards(locationsData.filter(location => location !== null));
        console.log("Updated cards data:", locationsData);

        setLoading(false);

        // Increment and log load count with timestamp
        loadCount += 1;
        const timestamp = new Date().toLocaleString();
        console.log(`Load ${loadCount} at ${timestamp}`);

        // Keep the spinner visible for 1 second after loading completes
        setTimeout(() => {
            setShowSpinner(false);
        }, 1000);
    };

    useEffect(() => {
        loadAllLocations();
        const interval = setInterval(loadAllLocations, 600000); // Check for updates every 10 minutes
        return () => clearInterval(interval);
    }, [edLocations]);

    if (showSpinner) {
        return (
            <div className="loading-spinner" style={{
                textAlign: 'center',
                marginTop: '20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#13294b' }}></i>
                <p>Refreshing data...</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="loading-spinner" style={{
                textAlign: 'center',
                marginTop: '20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#13294b' }}></i>
                <p>Refreshing data...</p>
            </div>
        );
    }

    // Render individual cards even if no wait time data is available
    return (
        <div id="mobile">
            {cards.map((card: any) => (
                <LocationCard
                    {...card}
                    key={card.edLocation + (card.epicDataLastUpdatedAt || 'no-update') + (card.waitTimeCategory || 'no-category') + card.waitTimeCategoryColor}
                />
            ))}
        </div>
    );
}

document.addEventListener("readystatechange", () => {
    if (document.readyState === "interactive" || document.readyState === "complete") {
        observe('[data-component="locationcards"]', {
            add(element: any) {
                const { dataset } = element;
                const { propsId } = dataset;
                const componentData = (window as any).ucsdh[`locationcards_${propsId}`];
                render(<LocationCards {...componentData} />, element);
            },
        });
    }
});
